import { useState, useEffect, useContext } from 'react';
import { AppContext, useApp } from '../App';
import api from '../api';
import { useI18n } from '../i18n/index.jsx';

export default function DashboardOwner() {
  const { user, setPage, isAdmin, currentCompany, isSuperuser, testMode, toggleTestMode } = useApp();
  const { t } = useI18n();
  const [stats, setStats] = useState(null);
  const [taxes, setTaxes] = useState(null);
  const [declarations, setDeclarations] = useState([]);
  const [expandedSections, setExpandedSections] = useState({ quick: true, accounting: true, hr: true });

  useEffect(() => { loadData(); }, [currentCompany?.id]);

  async function loadData() {
    try {
      const cid = currentCompany?.id;
      const qs = cid ? `?company_id=${cid}` : '';
      const [taxRes, declRes] = await Promise.all([
        api.get(`/accounting/my/realtime${qs}`).catch(() => null),
        api.get(`/accounting/my/declarations${qs}`).catch(() => null),
      ]);
      if (taxRes) setTaxes(taxRes.data);
      if (declRes) setDeclarations(declRes.data);
    } catch {}
  }

  function toggleSection(id) {
    setExpandedSections(p => ({ ...p, [id]: !p[id] }));
  }

  const now = new Date();

  const urgentDecl = declarations.filter(d => d.days_left <= 14 && d.days_left >= 0);
  const overdueDecl = declarations.filter(d => d.days_left < 0);

  return (
    <div className="dashboard">
      {/* Superuser: switch to client view */}
      {isSuperuser && (
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '8px 12px', marginBottom: 8, borderRadius: 12,
          background: testMode ? 'rgba(245,158,11,0.2)' : 'rgba(139,92,246,0.15)',
          border: `1px solid ${testMode ? 'rgba(245,158,11,0.3)' : 'rgba(139,92,246,0.3)'}`,
        }}>
          <span style={{ fontSize: 12 }}>
            {testMode ? '👤 Режим клиента' : '🔧 Режим администратора'}
          </span>
          <button onClick={toggleTestMode} className="btn btn-small" style={{ padding: '4px 10px', fontSize: 11 }}>
            {testMode ? '🔧 Включить админ' : '👤 Как клиент'}
          </button>
        </div>
      )}

      {/* Header */}
      <div style={{
        padding: '20px 16px', marginBottom: 16,
        background: 'linear-gradient(135deg, rgba(16,185,129,0.15) 0%, rgba(5,150,105,0.05) 100%)',
        borderRadius: 16, borderLeft: '4px solid #10b981',
      }}>
        <div style={{ fontSize: 20, fontWeight: 700 }}>📊 {user?.first_name || t('dashboard_owner.cabinet')}</div>
        <div style={{ fontSize: 13, opacity: 0.6, marginTop: 4 }}>
          {t('dashboard_owner.accounting_subtitle')} • {t(`calendar.months.${now.getMonth()}`)} {now.getFullYear()}
        </div>
      </div>

      {/* AI Assistant Card */}
      <div style={{
        background: 'linear-gradient(135deg, #6366f1, #ec4899)',
        borderRadius: 16, padding: 24, marginBottom: 16,
        textAlign: 'center', cursor: 'pointer',
        transition: 'transform 0.2s',
      }} onClick={() => setPage('ai_assistant')}
         onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.02)'}
         onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'}>
        <div style={{ fontSize: 48 }}>🤖</div>
        <h2 style={{ margin: '8px 0 4px', fontSize: 20, fontWeight: 700 }}>{t('dashboard_owner.ai_assistant')}</h2>
        <p style={{ margin: 0, opacity: 0.9, fontSize: 14 }}>
          {t('dashboard_owner.ai_desc')}
        </p>
        <div style={{ marginTop: 12, fontSize: 13, opacity: 0.8 }}>
          🎤 {t('dashboard_owner.ai_hint')}
        </div>
      </div>

      {/* Alerts */}
      {overdueDecl.length > 0 && (
        <div style={{
          padding: 12, borderRadius: 12, marginBottom: 12,
          background: 'rgba(239,68,68,0.15)', border: '1px solid rgba(239,68,68,0.3)',
        }}>
          <div style={{ fontWeight: 700, color: '#f87171' }}>🔴 {t('dashboard_owner.overdue')} ({overdueDecl.length})</div>
          {overdueDecl.map(d => (
            <div key={d.id} style={{ fontSize: 12, marginTop: 4 }}>
              {d.declaration_type} — {d.period} — {d.tax_amount.toLocaleString()} PLN ({t('dashboard_owner.deadline_was')} {d.due_date})
            </div>
          ))}
        </div>
      )}

      {urgentDecl.length > 0 && (
        <div style={{
          padding: 12, borderRadius: 12, marginBottom: 12,
          background: 'rgba(245,158,11,0.15)', border: '1px solid rgba(245,158,11,0.3)',
        }}>
          <div style={{ fontWeight: 700, color: '#f59e0b' }}>🟡 {t('dashboard_owner.urgent')} ({urgentDecl.length})</div>
          {urgentDecl.map(d => (
            <div key={d.id} style={{ fontSize: 12, marginTop: 4 }}>
              {d.declaration_type} — {d.period} — {d.tax_amount.toLocaleString()} PLN ({t('dashboard_owner.remained')} {d.days_left} {t('dashboard_owner.days_left')})
            </div>
          ))}
        </div>
      )}

      {/* Tax Summary Cards */}
      {taxes && (
        <div style={{
          display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 10, marginBottom: 16,
        }}>
          <div style={{ padding: 14, borderRadius: 12, background: 'rgba(99,102,241,0.1)', border: '1px solid rgba(99,102,241,0.2)' }}>
            <div style={{ fontSize: 11, opacity: 0.6 }}>{t('dashboard_owner.turnover_gross')}</div>
            <div style={{ fontSize: 18, fontWeight: 700, color: '#818cf8' }}>{(taxes.turnover || 0).toLocaleString()} PLN</div>
          </div>
          <div style={{ padding: 14, borderRadius: 12, background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.2)' }}>
            <div style={{ fontSize: 11, opacity: 0.6 }}>{t('dashboard_owner.expenses')}</div>
            <div style={{ fontSize: 18, fontWeight: 700, color: '#f87171' }}>{(taxes.expenses || 0).toLocaleString()} PLN</div>
          </div>
          <div style={{ padding: 14, borderRadius: 12, background: 'rgba(16,185,129,0.1)', border: '1px solid rgba(16,185,129,0.2)' }}>
            <div style={{ fontSize: 11, opacity: 0.6 }}>{t('dashboard_owner.vat_due')}</div>
            <div style={{ fontSize: 18, fontWeight: 700, color: '#10b981' }}>{(taxes.vat?.due || 0).toLocaleString()} PLN</div>
          </div>
          <div style={{ padding: 14, borderRadius: 12, background: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.2)' }}>
            <div style={{ fontSize: 11, opacity: 0.6 }}>{t('dashboard_owner.pit_advance')}</div>
            <div style={{ fontSize: 18, fontWeight: 700, color: '#f59e0b' }}>{(taxes.pit?.due || 0).toLocaleString()} PLN</div>
          </div>
        </div>
      )}

      {/* Quick Actions */}
      <div style={{ marginBottom: 16 }}>
        <div
          onClick={() => toggleSection('quick')}
          style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            padding: '12px 16px', background: 'linear-gradient(135deg, #6366f122 0%, #6366f111 100%)',
            borderRadius: 12, cursor: 'pointer', borderLeft: '4px solid #6366f1',
            marginBottom: expandedSections.quick ? 8 : 0,
          }}
        >
          <span style={{ fontWeight: 'bold' }}>⚡ {t('dashboard_owner.quick_actions')}</span>
          <span style={{ transform: expandedSections.quick ? 'rotate(180deg)' : 'rotate(0)', transition: 'transform 0.2s', opacity: 0.6 }}>▼</span>
        </div>
        {expandedSections.quick && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
            {[
              { id: 'owner_invoices', icon: '📄', label: t('dashboard_owner.invoices') },
              { id: 'hr_employees', icon: '👷', label: t('dashboard_owner.employees') },
              { id: 'counterparties', icon: '🤝', label: t('dashboard_owner.counterparties') },
              { id: 'my_finance', icon: '🏦', label: t('dashboard_owner.accounts') },
              { id: 'company_team', icon: '👥', label: t('dashboard_owner.team') },
              { id: 'calendar', icon: '📅', label: t('dashboard_owner.calendar') },
            ].map(a => (
              <div key={a.id} onClick={() => setPage(a.id)} style={{
                padding: '14px 8px', borderRadius: 10, textAlign: 'center', cursor: 'pointer',
                background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.06)',
              }}>
                <div style={{ fontSize: 24 }}>{a.icon}</div>
                <div style={{ fontSize: 11, marginTop: 4, opacity: 0.8 }}>{a.label}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Accounting */}
      <div style={{ marginBottom: 16 }}>
        <div
          onClick={() => toggleSection('accounting')}
          style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            padding: '12px 16px', background: 'linear-gradient(135deg, #f59e0b22 0%, #f59e0b11 100%)',
            borderRadius: 12, cursor: 'pointer', borderLeft: '4px solid #f59e0b',
            marginBottom: expandedSections.accounting ? 8 : 0,
          }}
        >
          <span style={{ fontWeight: 'bold' }}>📊 {t('dashboard_owner.accounting_taxes')}</span>
          <span style={{ transform: expandedSections.accounting ? 'rotate(180deg)' : 'rotate(0)', transition: 'transform 0.2s', opacity: 0.6 }}>▼</span>
        </div>
        {expandedSections.accounting && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 10 }}>
            {[
              { id: 'owner_invoices', icon: '📄', title: t('dashboard_owner.invoices'), desc: 'Proforma / VAT / Korekta' },
              { id: 'my_taxes', icon: '🧮', title: t('dashboard_owner.taxes'), desc: 'VAT / PIT / CIT / ZUS' },
              { id: 'owner_cit8', icon: '🏛', title: 'CIT-8', desc: t('dashboard_owner.reports') },
              { id: 'tax_dashboard', icon: '📊', title: 'Sprawozdanie', desc: 'RZiS / Bilans / PDF' },
              { id: 'admin_bank_import', icon: '🏦', title: t('dashboard_owner.bank_import'), desc: t('dashboard.admin_bank_import_desc') },
              { id: 'counterparties', icon: '🤝', title: t('dashboard_owner.counterparties'), desc: t('dashboard.menu_counterparties_desc') },
              { id: 'contract_templates', icon: '📑', title: t('dashboard_owner.templates'), desc: t('dashboard.menu_templates_desc') },
              { id: 'bank_accounts', icon: '🏦', title: t('dashboard_owner.bank_accounts'), desc: t('bank_accounts.title') },
              { id: 'accounting_restore', icon: '🔄', title: t('accounting_restore.title'), desc: t('accounting_restore.subtitle') },
              { id: 'ksef_settings', icon: '🔑', title: t('dashboard_owner.ksef'), desc: 'e-Faktura' },
              { id: 'stripe_settings', icon: '💳', title: t('dashboard_owner.stripe'), desc: t('dashboard.admin_payments_desc') },
            ].map(card => (
              <div key={card.id} onClick={() => setPage(card.id)} style={{
                padding: 14, borderRadius: 12, cursor: 'pointer',
                background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)',
              }}>
                <div style={{ fontSize: 24, marginBottom: 6 }}>{card.icon}</div>
                <div style={{ fontSize: 13, fontWeight: 600 }}>{card.title}</div>
                <div style={{ fontSize: 11, opacity: 0.5, marginTop: 2 }}>{card.desc}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* HR */}
      <div style={{ marginBottom: 16 }}>
        <div
          onClick={() => toggleSection('hr')}
          style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            padding: '12px 16px', background: 'linear-gradient(135deg, #10b98122 0%, #10b98111 100%)',
            borderRadius: 12, cursor: 'pointer', borderLeft: '4px solid #10b981',
            marginBottom: expandedSections.hr ? 8 : 0,
          }}
        >
          <span style={{ fontWeight: 'bold' }}>👥 {t('dashboard_owner.hr_personnel')}</span>
          <span style={{ transform: expandedSections.hr ? 'rotate(180deg)' : 'rotate(0)', transition: 'transform 0.2s', opacity: 0.6 }}>▼</span>
        </div>
        {expandedSections.hr && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 10 }}>
            {[
              { id: 'hr_employees', icon: '👷', title: t('dashboard_owner.employees'), desc: t('dashboard.menu_employees_desc') },
              { id: 'zus_dashboard', icon: '🏦', title: t('dashboard_owner.zus_dashboard'), desc: t('zus_dashboard.subtitle') },
              { id: 'company_team', icon: '🔑', title: t('dashboard_owner.team'), desc: t('dashboard.menu_team_desc') },
              { id: 'a1_application', icon: '🇪🇺', title: 'Zaświadczenie A1', desc: 'Delegowanie pracowników' },
              { id: 'calendar', icon: '📅', title: t('dashboard_owner.calendar'), desc: t('dashboard.menu_calendar_desc') },
              { id: 'ai_calls', icon: '🤖', title: 'AI Звонки', desc: 'Звонки с ИИ-агентом' },
              { id: 'support', icon: '📞', title: t('dashboard_owner.support'), desc: t('dashboard.menu_support_desc') },
            ].map(card => (
              <div key={card.id} onClick={() => setPage(card.id)} style={{
                padding: 14, borderRadius: 12, cursor: 'pointer',
                background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)',
              }}>
                <div style={{ fontSize: 24, marginBottom: 6 }}>{card.icon}</div>
                <div style={{ fontSize: 13, fontWeight: 600 }}>{card.title}</div>
                <div style={{ fontSize: 11, opacity: 0.5, marginTop: 2 }}>{card.desc}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Settings row */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        <div onClick={() => setPage('security')} style={{
          flex: 1, padding: 12, borderRadius: 10, textAlign: 'center', cursor: 'pointer',
          background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)',
        }}>
          🔐 {t('dashboard_owner.security')}
        </div>
        <div onClick={() => setPage('notifications')} style={{
          flex: 1, padding: 12, borderRadius: 10, textAlign: 'center', cursor: 'pointer',
          background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)',
        }}>
          🔔 {t('dashboard_owner.notifications')}
        </div>
        <div onClick={() => setPage('profile')} style={{
          flex: 1, padding: 12, borderRadius: 10, textAlign: 'center', cursor: 'pointer',
          background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)',
        }}>
          👤 {t('dashboard_owner.profile')}
        </div>
      </div>

      {/* Admin panel for superadmin */}
      {isAdmin && (
        <div style={{ marginBottom: 16 }}>
          <div
            onClick={() => toggleSection('admin')}
            style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              padding: '12px 16px', background: 'linear-gradient(135deg, #ef444422 0%, #ef444411 100%)',
              borderRadius: 12, cursor: 'pointer', borderLeft: '4px solid #ef4444',
              marginBottom: expandedSections.admin ? 8 : 0,
            }}
          >
            <span style={{ fontWeight: 'bold' }}>⚙️ {t('dashboard_owner.admin_panel')}</span>
            <span style={{ transform: expandedSections.admin ? 'rotate(180deg)' : 'rotate(0)', transition: 'transform 0.2s', opacity: 0.6 }}>▼</span>
          </div>
          {expandedSections.admin && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 10 }}>
              {[
                { id: 'admin_users', icon: '👥', title: t('dashboard_owner.users'), desc: t('dashboard.admin_users_desc') },
                { id: 'admin_tier2', icon: '🏗️', title: t('dashboard_owner.tier2_companies'), desc: t('dashboard.admin_tier2_desc') },
                { id: 'admin_companies', icon: '🏢', title: t('dashboard_owner.companies'), desc: t('dashboard.admin_companies_desc') },
                { id: 'admin_accounting', icon: '📊', title: t('dashboard_owner.accounting'), desc: 'VAT / PIT / CIT' },
                { id: 'admin_analytics', icon: '📈', title: t('dashboard_owner.analytics'), desc: t('dashboard.admin_analytics_desc') },
                { id: 'admin_audit', icon: '📋', title: t('dashboard_owner.audit'), desc: t('dashboard.admin_audit_desc') },
              ].map(card => (
                <div key={card.id} onClick={() => setPage(card.id)} style={{
                  padding: 14, borderRadius: 12, cursor: 'pointer',
                  background: 'rgba(239,68,68,0.05)', border: '1px solid rgba(239,68,68,0.15)',
                }}>
                  <div style={{ fontSize: 24, marginBottom: 6 }}>{card.icon}</div>
                  <div style={{ fontSize: 13, fontWeight: 600 }}>{card.title}</div>
                  <div style={{ fontSize: 11, opacity: 0.5, marginTop: 2 }}>{card.desc}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
